/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import { PHOTO_PATH } from "../config";

/**
 * Where a stored photo is served from.
 *
 * One base for every image in the app: REACT_APP_PHOTO_PATH, which is set per
 * environment alongside the API endpoint. The org logo and the project header
 * have always been built this way — the upload returns the S3 key and the
 * client puts the base in front of it (see OrgSettings and OrgSetupLogo).
 *
 * The Impact & proof page did not, and that is what broke it. Its endpoints
 * built the whole URL server-side from `checkplus/photo-url`, a second secret
 * that decides nothing about where multer actually writes — the bucket comes
 * from `checkplus/photo-bucket-name`. The two drifted, so covers and post
 * photos were uploaded to one bucket and linked to another, and every image
 * on the page 404'd while the rest of the app was unaffected. There is now
 * one answer to "where are photos served from", and it is this file.
 *
 * An absolute value is handed back untouched. Two callers need that: rows
 * written before this existed hold whole URLs (a project's
 * headerBackgroundPicture, a payment code), and a blob: preview of a file
 * that has not been uploaded yet is not a key either.
 */
export const photoUrl = (key) => {
  if (!key) {
    return "";
  }
  if (/^(https?:)?\/\/|^blob:|^data:/i.test(key)) {
    return key;
  }
  // An unset base leaves the key as it is rather than prefixing the string
  // "undefined" onto it — which is what `PHOTO_PATH + key` did, and what made
  // a missing environment variable look like a missing image.
  if (!PHOTO_PATH) {
    return key;
  }
  // The keys carry no leading slash, so a base configured without its trailing
  // one produced `https://host/bucketKEY` — a URL that resolves to nothing,
  // with no error anywhere and an empty image box at the far end. The same
  // normalisation the API does on its side (getS3MediaUrl in platformHelper),
  // kept here too because this base is now the one these images are built on.
  const base = PHOTO_PATH.endsWith("/") ? PHOTO_PATH : PHOTO_PATH + "/";
  return base + key.replace(/^\//, "");
};

export default photoUrl;
